import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Scroll-triggered reveal for the contact section: title, subtitle, form blocks, and mood side.
 */
export class ContactSectionReveal {
  private section: HTMLElement | null = null;
  private title: HTMLElement | null = null;
  private subtitle: HTMLElement | null = null;
  private form: HTMLElement | null = null;
  private formFields: HTMLElement[] = [];
  private moodContent: HTMLElement | null = null;
  private timeline: gsap.core.Timeline | null = null;

  constructor() {
    this.section = document.querySelector('.contact-section');
    if (!this.section) return;

    this.title = this.section.querySelector('.contact-title');
    this.subtitle = this.section.querySelector('.contact-subtitle');
    this.form = this.section.querySelector('.contact-form');
    this.moodContent = this.section.querySelector('.contact-mood-content');

    if (this.form) {
      this.formFields = Array.from(
        this.form.querySelectorAll('.contact-label, .contact-input, .contact-textarea, .contact-submit')
      );
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      gsap.set(
        [this.title, this.subtitle, this.form, this.moodContent].filter(Boolean),
        { opacity: 1, y: 0 }
      );
      if (this.formFields.length) gsap.set(this.formFields, { opacity: 1, y: 0 });
      return;
    }

    this.setInitialStates();
    this.setupScrollReveal();
  }

  private setInitialStates(): void {
    if (this.title) gsap.set(this.title, { opacity: 0, y: 28, force3D: true });
    if (this.subtitle) gsap.set(this.subtitle, { opacity: 0, y: 20, force3D: true });
    this.formFields.forEach((el) => gsap.set(el, { opacity: 0, y: 24, force3D: true }));
    if (this.moodContent) gsap.set(this.moodContent, { opacity: 0, y: 24, force3D: true });
  }

  private setupScrollReveal(): void {
    if (!this.section) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.section,
        start: 'top 88%',
        once: true,
        invalidateOnRefresh: true,
      },
      defaults: { ease: 'power3.out', force3D: true },
    });

    if (this.title) {
      tl.to(this.title, { opacity: 1, y: 0, duration: 0.6 }, 0);
    }
    if (this.subtitle) {
      tl.to(this.subtitle, { opacity: 1, y: 0, duration: 0.5 }, 0.12);
    }
    if (this.formFields.length) {
      tl.to(
        this.formFields,
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.06,
          ease: 'power3.out',
        },
        0.25
      );
    }
    if (this.moodContent) {
      tl.to(this.moodContent, { opacity: 1, y: 0, duration: 0.55 }, 0.2);
    }

    tl.set(
      [this.title, this.subtitle, this.moodContent, ...this.formFields].filter(Boolean),
      { clearProps: 'transform' }
    );

    this.timeline = tl;
  }

  dispose(): void {
    this.timeline?.scrollTrigger?.kill();
    this.timeline?.kill();
    this.timeline = null;
    this.section = null;
    this.title = null;
    this.subtitle = null;
    this.form = null;
    this.formFields = [];
    this.moodContent = null;
  }
}
